import { useId, type CSSProperties } from 'react';
import { useT } from '../i18n';
import { FAN_DEG, FOOTPRINT_M, HUMAN_RANGE_M, fmtDist, type SensorView, type Track } from '../lib/sensors';
import { DEFAULT_LAYERS, STATE_COLOR, bandColor, lidarCaption, stateLabel, type RadarLayers } from '../lib/radar';
import type { LidarStatus, LidarView } from '../lib/lidar';
import type { GuardStatus } from '../api/types';

const TREND_COLOR = { approaching: '#E5534B', static: '#9BA1A8', receding: '#4CB782' } as const;

const f = (n: number) => String(Math.round(n * 10) / 10);
const rad = (b: number) => (b * Math.PI) / 180;

/** Geometry helpers for a radar centred at (cx, cy) with K px per meter; bearing 0 = forward (up), + = right. */
function geo(cx: number, cy: number, K: number) {
  const pt = (d: number, b: number): [number, number] => [cx + d * K * Math.sin(rad(b)), cy - d * K * Math.cos(rad(b))];
  const P = (a: [number, number]) => `${f(a[0])} ${f(a[1])}`;
  const wedge = (r0: number, r1: number, b0: number, b1: number) => {
    const lg = b1 - b0 > 180 ? 1 : 0;
    return `M${P(pt(r0, b0))}L${P(pt(r1, b0))}A${f(r1 * K)} ${f(r1 * K)} 0 ${lg} 1 ${P(pt(r1, b1))}L${P(pt(r0, b1))}A${f(r0 * K)} ${f(r0 * K)} 0 ${lg} 0 ${P(pt(r0, b0))}Z`;
  };
  const arc = (r: number, b0: number, b1: number) => `M${P(pt(r, b0))}A${f(r * K)} ${f(r * K)} 0 0 1 ${P(pt(r, b1))}`;
  return { pt, P, wedge, arc };
}

/** One depth sector + track blips, shared by the radar and the map overlay. */
function DepthAndTracks({ view, cx, cy, K, layers, hatchId, small, mapMode }: { view: SensorView; cx: number; cy: number; K: number; layers: RadarLayers; hatchId: string; small: boolean; mapMode?: boolean }) {
  const { pt, P, wedge, arc } = geo(cx, cy, K);
  const R0 = FOOTPRINT_M;
  const max = view.maxM;
  const dots = (t: Track) =>
    t.trail
      .map(([d, b], k) => {
        const [x, y] = pt(d, b);
        const r = small ? 1.3 : 2 + k * 0.6;
        return `M${f(x - r)} ${f(y)}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`;
      })
      .join('');
  return (
    <>
      {layers.depth && (
        <g>
          {mapMode && <path d={wedge(R0, max, -FAN_DEG / 2, FAN_DEG / 2)} fill="#5AA9E6" fillOpacity={0.12} stroke="#5AA9E6" strokeOpacity={0.4} strokeWidth={1} />}
          {view.sectors.map((s) => (
            <g key={s.i}>
              <path d={wedge(R0, max, s.from + 0.5, s.to - 0.5)} fill={s.nd || !view.fresh ? `url(#${hatchId})` : s.d === null ? '#2BB3A3' : 'none'} fillOpacity={s.nd || !view.fresh ? 1 : s.d === null ? 0.07 : 1} stroke="#2E333A" strokeWidth={1} />
              {view.fresh && s.d !== null && (
                // filtered distance; a low-confidence sector is faded and its edge dashed
                <g opacity={s.confident ? 1 : 0.35}>
                  <path d={wedge(R0, Math.max(R0 + 0.02, s.d), s.from + 0.5, s.to - 0.5)} fill={bandColor(s.d)} fillOpacity={s.confident ? 0.24 : 0.12} />
                  <path d={arc(s.d, s.from + 0.8, s.to - 0.8)} fill="none" stroke={bandColor(s.d)} strokeWidth={small ? 2 : 3} strokeLinecap="round" strokeDasharray={s.confident ? undefined : '3 3'} />
                </g>
              )}
              {view.fresh && s.d === null && !s.nd && <path d={arc(max, s.from + 0.8, s.to - 0.8)} fill="none" stroke="#2BB3A3" strokeOpacity={0.5} strokeWidth={1.5} strokeDasharray="3 3" />}
            </g>
          ))}
        </g>
      )}
      {layers.tracks &&
        view.tracks.map((t) => {
          const c = TREND_COLOR[t.trend];
          const v = t.trend === 'static' ? null : `M${P(pt(t.d, t.bearing))}L${P(pt(Math.max(R0, t.d + (t.trend === 'approaching' ? -1 : 1) * t.speed * 1.5), t.bearing))}`;
          return (
            <g key={t.id}>
              {t.trail.length > 0 && <path d={dots(t)} fill={c} fillOpacity={0.45} />}
              {v && <path d={v} fill="none" stroke={c} strokeWidth={2} strokeDasharray="4 3" />}
            </g>
          );
        })}
    </>
  );
}

/** §27 live lidar, raw: every return the lidar reports, as one path of small squares (no object overlay). */
function LidarLayer({ lidar, cx, cy, K, R, full }: { lidar: LidarView; cx: number; cy: number; K: number; R: number; full: boolean }) {
  const { pt } = geo(cx, cy, K);
  const s = full ? (R > 10 ? 1.4 : 1.8) : 1.2;
  let walls = '';
  for (const [b, r] of lidar.points) {
    if (r > R) continue;
    const [x, y] = pt(r, b);
    walls += `M${f(x - s / 2)} ${f(y - s / 2)}h${s}v${s}h${-s}z`;
  }
  return <path d={walls} fill="#7FB3D5" fillOpacity={0.85} />;
}

const LIDAR_BADGE: Record<LidarStatus, string> = { off: '#5C636B', connecting: '#5AA9E6', live: '#4CB782', moving: '#F0A04B', nodata: '#F0A04B' };

/** Lidar centre → robot body (m): the guard measures its distance from the body (PROTOCOL §25). */
const GUARD_BODY_M = 0.3;

/**
 * The guard's alarm border (§25): a red ring at the guard distance from the body — crossing it raises the warning and,
 * a second later, the siren. Brighter while the guard warns / alarms; grey and dashed while it is paused.
 */
function GuardBarrier({ guard, cx, cy, K, R, full, units }: { guard: GuardStatus; cx: number; cy: number; K: number; R: number; full: boolean; units: 'm' | 'cm' }) {
  const t = useT();
  const d = guard.warnDistance ?? 1.5;
  const r = d + GUARD_BODY_M;
  if (r > R + 0.3) return null;
  const paused = Boolean(guard.paused);
  const hot = !paused && (guard.level === 'WARN' || guard.level === 'ALARM' || guard.level === 'TOUCH');
  const c = paused ? '#7A828B' : '#E5534B';
  const [lx, ly] = geo(cx, cy, K).pt(r, -135);
  return (
    <g>
      <circle cx={cx} cy={cy} r={r * K} fill={c} fillOpacity={paused ? 0 : hot ? 0.16 : 0.05} stroke={c} strokeOpacity={hot ? 1 : 0.85} strokeWidth={full ? (hot ? 3 : 2) : hot ? 2 : 1.5} strokeDasharray={paused ? '6 5' : undefined} />
      {full && (
        <text x={lx - 6} y={ly - 6} textAnchor="end" fontFamily="var(--font-mono)" fontSize="11" fontWeight={600} fill={paused ? '#9BA1A8' : '#FF8A80'}>
          {t('guard.barrier', { d: fmtDist(d, units, 1) })}
        </text>
      )}
    </g>
  );
}

/** Ring spacing for a lidar radar of range `R` metres. */
const lidarRings = (R: number) => {
  const step = R <= 2 ? 0.5 : R <= 5 ? 1 : R <= 10 ? 2 : R <= 20 ? 5 : 10;
  const out: number[] = [];
  for (let d = step; d <= R + 1e-6; d += step) out.push(Math.round(d * 10) / 10);
  return out;
};

/** Blip markers + labels as positioned HTML (CSS pulse animation). */
function Blips({ view, cx, cy, K, units, size, variant, idle }: { view: SensorView; cx: number; cy: number; K: number; units: 'm' | 'cm'; size: number; variant: 'full' | 'mini' | 'map'; idle: boolean }) {
  const t = useT();
  const { pt } = geo(cx, cy, K);
  const full = variant === 'full';
  return (
    <>
      {view.tracks.map((tr) => {
        const [x, y] = pt(tr.d, tr.bearing);
        const c = TREND_COLOR[tr.trend];
        const m = full ? (tr.kind === 'person' ? 14 : 12) : variant === 'map' ? 9 : 8;
        const showLabel = (full && (size >= 480 || tr.trend !== 'static')) || (variant === 'map' && tr.trend === 'approaching');
        const LW = full ? 200 : 90;
        const right = x + 12 + LW <= size || x - 12 - LW < 0;
        const l2 =
          tr.trend === 'approaching'
            ? t('sens.lblApproach', { v: tr.speed.toFixed(1), ttc: tr.ttc !== null ? tr.ttc.toFixed(1) : '—' })
            : tr.trend === 'receding'
              ? t('sens.lblAway', { v: tr.speed.toFixed(1) })
              : t('sens.lblStatic');
        return (
          <span key={tr.id}>
            {tr.trend === 'approaching' && !idle && <span className="rd-pulse" style={{ left: x - m, top: y - m, width: 2 * m, height: 2 * m, borderColor: c }} />}
            <span className="rd-blip" style={{ left: x - m / 2, top: y - m / 2, width: m, height: m, background: c, borderRadius: tr.kind === 'person' ? '50%' : 3 }} />
            {showLabel && (
              <span
                className={`rd-label${tr.trend === 'approaching' ? ' hot' : ''}`}
                style={{ top: Math.max(2, y - 18), ...(right ? { left: Math.min(x + 12, Math.max(2, size - LW)) } : { left: Math.max(2, x - 12 - LW) }), maxWidth: LW, borderColor: tr.trend === 'approaching' ? `${c}99` : undefined }}
              >
                <b>{full ? `${tr.kind === 'person' ? t('sens.person') : t('sens.object')} · ${fmtDist(tr.d, units, 2)}` : `${fmtDist(tr.d, units, 1)} ↓`}</b>
                {full && <span style={{ color: tr.trend === 'static' ? '#9BA1A8' : c }}>{l2}</span>}
                {full && (
                  <span className="muted">
                    {tr.id} · {t(`sens.src.${tr.source}`)} · {Math.round(tr.conf * 100)}%
                  </span>
                )}
              </span>
            )}
          </span>
        );
      })}
    </>
  );
}

/**
 * Proximity radar (design "Proximity radar"): front depth fan (6 sectors), human_detect ring (no direction),
 * derived tracks, hatched blind zones. `full` = Sensors page, `mini` = round badge with a caption.
 */
export function Radar({ view, size, variant = 'full', layers = DEFAULT_LAYERS, units = 'm', threshold = 1, className, style, lidar = null, lidarRange = 4, guard = null }: { view: SensorView; size: number; variant?: 'full' | 'mini'; layers?: RadarLayers; units?: 'm' | 'cm'; threshold?: number; className?: string; style?: CSSProperties; lidar?: LidarView | null; lidarRange?: number; guard?: GuardStatus | null }) {
  const t = useT();
  const uid = useId().replace(/:/g, '');
  const S = size;
  const full = variant === 'full';
  const mini = !full;
  // §27: with the lidar layer the radar is a centred 360° view of `lidarRange` metres
  const lidarOn = Boolean(lidar && lidar.status !== 'off');
  const lidarSees = lidarOn && (lidar!.status === 'live' || lidar!.status === 'moving');
  const R = lidarOn ? lidarRange : Math.max(view.maxM, HUMAN_RANGE_M) + 0.25;
  const cx = S / 2;
  const cy = lidarOn ? S / 2 : full ? S * 0.74 : S * 0.68;
  const K = lidarOn ? (S / 2 - (full ? 14 : 6)) / R : full ? (cy - 18) / R : (cy - 8) / R;
  const rings = lidarOn ? lidarRings(R) : [0.5, 1, view.maxM];
  // §25 the guard's alarm border replaces the site-side "approach alert" ring while the guard is on
  const guardOn = Boolean(guard?.enabled && typeof guard.warnDistance === 'number');
  const { pt, wedge } = geo(cx, cy, K);
  const camOn = layers.cam !== false;
  // without the camera layer, people that were only seen by the camera are hidden (fused ones stay)
  const shown: SensorView = camOn ? view : { ...view, tracks: view.tracks.filter((x) => x.source !== 'camera') };
  const idle = view.state === 'charging';
  const sc = STATE_COLOR[view.state];
  const near = Boolean(view.human?.near) && !idle;
  const big = S * 3;
  const blindD = `M${-big} ${-big}H${big}V${big}H${-big}Z${wedge(0.001, R * 4, -FAN_DEG / 2, FAN_DEG / 2)}`;
  const lab = (d: number) => (lidarOn ? pt(d, 45) : pt(d, FAN_DEG / 2 + 4));
  const fmtRing = (d: number) => (units === 'cm' ? `${Math.round(d * 100)} cm` : `${d} m`);
  const discStyle: CSSProperties = mini
    ? lidarOn
      ? { borderColor: '#5AA9E6', borderStyle: lidar!.status === 'connecting' ? 'dashed' : 'solid' }
      : { borderColor: sc, borderStyle: view.state === 'nodata' ? 'dashed' : 'solid' }
    : {};
  const glow = mini && (view.state === 'approaching' ? 'rd-glow' : view.state === 'tooClose' || view.state === 'cliff' ? 'rd-glow-red' : '');
  return (
    <div className={`radar ${variant} ${className ?? ''}`} style={{ width: S, height: mini ? S + 32 : S, ...style }} role="img" aria-label={lidarOn ? lidarCaption(t, lidar!, units) : stateLabel(t, view, units)}>
      <div className={`radar-disc ${glow || ''}`} style={{ width: S, height: S, ...discStyle }}>
        <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} aria-hidden>
          <defs>
            <pattern id={`h${uid}`} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <path d="M0 0V7" stroke="#3A4048" strokeWidth="2" />
            </pattern>
            <pattern id={`n${uid}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#1A1D21" />
              <path d="M0 0V6" stroke="#5C636B" strokeWidth="2" />
            </pattern>
          </defs>
          <g opacity={idle ? 0.45 : 1}>
            {!lidarSees && <path d={blindD} fill={`url(#h${uid})`} fillRule="evenodd" opacity={0.55} />}
            {rings.map((d) => (
              <circle key={d} cx={cx} cy={cy} r={d * K} fill="none" stroke={!lidarOn && d === view.maxM ? '#2A2E34' : '#343941'} strokeWidth={1} />
            ))}
            {full && threshold <= R && !guardOn && <circle cx={cx} cy={cy} r={threshold * K} fill="none" stroke="#E5534B" strokeOpacity={0.6} strokeWidth={1.2} strokeDasharray="5 4" />}
            {camOn && view.people && (
              <path
                d={wedge(FOOTPRINT_M, R, -view.people.hfovDeg / 2, view.people.hfovDeg / 2)}
                fill="#5AA9E6"
                fillOpacity={view.people.active ? 0.05 : 0}
                stroke="#5AA9E6"
                strokeOpacity={view.people.active ? 0.5 : 0.25}
                strokeDasharray="3 4"
              />
            )}
            <DepthAndTracks view={shown} cx={cx} cy={cy} K={K} layers={layers} hatchId={`n${uid}`} small={mini} />
            {layers.pres && (
              <circle cx={cx} cy={cy} r={HUMAN_RANGE_M * K} fill={near ? '#F0A04B' : 'none'} fillOpacity={0.1} stroke={near ? '#F0A04B' : '#4A5058'} strokeWidth={near ? 2 : 1.2} strokeDasharray="2 5" strokeLinecap="round" />
            )}
            <circle cx={cx} cy={cy} r={FOOTPRINT_M * K} fill="#1D2024" stroke="#5AA9E6" strokeWidth={2} />
            <path
              d={`M${f(cx)} ${f(cy - FOOTPRINT_M * K * 0.75)}L${f(cx + FOOTPRINT_M * K * 0.42)} ${f(cy + FOOTPRINT_M * K * 0.3)}L${f(cx)} ${f(cy + FOOTPRINT_M * K * 0.08)}L${f(cx - FOOTPRINT_M * K * 0.42)} ${f(cy + FOOTPRINT_M * K * 0.3)}Z`}
              fill="#5AA9E6"
            />
            {idle && (
              <>
                <circle cx={pt(FOOTPRINT_M + 0.06, 170)[0]} cy={pt(FOOTPRINT_M + 0.06, 170)[1]} r={full ? 3.5 : 2} fill="#F0C24B" />
                <circle cx={pt(FOOTPRINT_M + 0.06, 190)[0]} cy={pt(FOOTPRINT_M + 0.06, 190)[1]} r={full ? 3.5 : 2} fill="#F0C24B" />
              </>
            )}
          </g>
          {guardOn && <GuardBarrier guard={guard!} cx={cx} cy={cy} K={K} R={R} full={full} units={units} />}
          {lidarSees && <LidarLayer lidar={lidar!} cx={cx} cy={cy} K={K} R={R} full={full} />}
          {full && (
            <g fill="#6B7178" fontFamily="var(--font-mono)" fontSize="11">
              {rings.map((d) => (
                <text key={d} x={lab(d)[0] + 4} y={lab(d)[1] + 4}>
                  {fmtRing(d)}
                </text>
              ))}
              {layers.pres && (
                <text x={12} y={S - 12} fill={near ? '#F0A04B' : '#6B7178'}>
                  human_detect · {near ? 'NEAR' : view.human ? 'none' : '—'} · {t('sens.noDirection')}
                </text>
              )}
            </g>
          )}
        </svg>
        {full && !lidarSees && (
          <>
            <span className="rd-blind" style={{ left: '50%', top: cy + 0.55 * K, transform: 'translateX(-50%)' }}>
              {t('sens.blindRear')}
            </span>
            <span className="rd-blind" style={{ left: 8, top: cy - 0.2 * K }}>
              {t('sens.blindSide')}
            </span>
            <span className="rd-blind" style={{ right: 8, top: cy - 0.2 * K }}>
              {t('sens.blindSide')}
            </span>
          </>
        )}
        {layers.tracks && <Blips view={shown} cx={cx} cy={cy} K={K} units={units} size={S} variant={variant} idle={idle} />}
        {view.state === 'cliff' && (
          <span className="rd-event" style={{ top: cy - FOOTPRINT_M * K - (full ? 40 : 22) }}>
            ⚠ {full ? t('sens.cliffBadge') : ''}
          </span>
        )}
        {lidarOn && !lidarSees && (
          <span className="rd-idle" style={{ top: cy - FOOTPRINT_M * K - (full ? 40 : 22), color: LIDAR_BADGE[lidar!.status] }}>
            {t(`lidar.badge.${lidar!.status}`, { hz: '' })}
          </span>
        )}
        {idle && !lidarOn && (
          <span className="rd-idle" style={{ top: cy - FOOTPRINT_M * K - (full ? 40 : 22) }}>
            ⚡ {mini ? t('sens.idleShort') : t('sens.capCharging')}
          </span>
        )}
        {view.state === 'nodata' && full && <span className="rd-nodata">{view.has ? t('sens.capStale') : t('sens.capNoSensors')}</span>}
      </div>
      {mini && (
        <div className="rd-caption" style={{ top: S + 6, borderColor: `${lidarOn ? '#5AA9E6' : sc}66` }}>
          <span className="dot" style={{ background: lidarOn ? LIDAR_BADGE[lidar!.status] : sc }} />
          {lidarOn ? lidarCaption(t, lidar!, units) : stateLabel(t, view, units)}
        </div>
      )}
    </div>
  );
}

/**
 * Map overlay: the front fan + blips around a robot marker, in map scale (`K` px per meter) and rotated with the
 * robot's heading (`rotation` degrees, 0 = +x, counter-clockwise; same convention as the marker).
 */
export function SensorFan({ view, x, y, K, rotation, units = 'm' }: { view: SensorView; x: number; y: number; K: number; rotation: number; units?: 'm' | 'cm' }) {
  const uid = useId().replace(/:/g, '');
  const R = view.maxM + 0.3;
  const S = Math.ceil(2 * R * K + 8);
  if (!view.has || S < 16) return null;
  const c = S / 2;
  const blipView = { ...view, tracks: view.tracks };
  return (
    <div className="sensor-fan" style={{ left: x - c, top: y - c, width: S, height: S, transform: `rotate(${90 - rotation}deg)` }} aria-hidden>
      <svg width={S} height={S}>
        <defs>
          <pattern id={`m${uid}`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="5" height="5" fill="#1A1D21" fillOpacity={0.6} />
            <path d="M0 0V5" stroke="#5C636B" strokeWidth="1.5" />
          </pattern>
        </defs>
        <g opacity={view.state === 'charging' ? 0.5 : 1}>
          <DepthAndTracks view={view} cx={c} cy={c} K={K} layers={{ depth: true, pres: false, tracks: true }} hatchId={`m${uid}`} small mapMode />
        </g>
      </svg>
      <Blips view={blipView} cx={c} cy={c} K={K} units={units} size={S} variant="map" idle={view.state === 'charging'} />
    </div>
  );
}
